import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';

import { DROPDOWN_FETCH_PAGE_SIZE } from '@klareospace/pharos-lib-front-utils';
import type { CarrierEnabledScope, UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { ROLE_SCOPE } from '@klareospace/pharos-lib-types';
import { isCarrierEnabledOnSite } from '@klareospace/pharos-lib-utils';
import type { MultiSelectOption } from '@klareospace/pharos-lib-web-utils';

import type { UseDriverVehicleFieldReturn } from '../../drivers/components/driver-vehicle-field';
import { useDriverVehicleField } from '../../drivers/components/driver-vehicle-field';
import { useCarrier, useCarriers } from '../../carriers/hooks';
import { getCarrierSiteFilterForSpace } from '../../carriers/utils';
import type { CarrierSiteFilter } from '../../carriers/utils';
import { withAssignedOption } from '../../shared/scope-filters';
import type { UserFormInput } from '../schemas/user-form.schema';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExistingSiteOption {
  label: string;
  parentSpaceId?: string;
  value: string;
}

export interface UseDriverFieldsParams {
  form: UseFormReturn<UserFormInput>;
  hasPermission: (permission: string) => boolean;
  isDriverRole: boolean;
  isTenantAdmin: boolean;
  lockedCarrierId?: string;
  lockedSiteId?: string;
  lockedSpaceId?: string;
  open: boolean;
  user: UserWithResolvedScopes | null;
}

export interface UseDriverFieldsReturn {
  carrierOptions: MultiSelectOption[];
  driverVehicleField: UseDriverVehicleFieldReturn;
  siteFilterForSpace: ((spaceId: string) => CarrierSiteFilter) | undefined;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Encapsulates every driver-role-dependent field concern shared by the unified user/driver form:
 * carrier options (scoped to the edited user's space, tenant-wide for TENANT_ADMIN), the advanced
 * vehicle picker, the carrier-derived site constraint for ScopeFields, and the two side effects
 * that keep siteIds/defaultVehicleId consistent when the carrier selection changes.
 */
export function useDriverFields({
  form,
  hasPermission,
  isDriverRole,
  isTenantAdmin,
  lockedCarrierId,
  lockedSiteId,
  lockedSpaceId,
  open,
  user,
}: UseDriverFieldsParams): UseDriverFieldsReturn {
  const isEditMode = user !== null;
  const selectedCarrierId = form.watch('carrierId') ?? undefined;
  const selectedSiteIds: string[] = form.watch('siteIds') ?? [];
  const selectedSpaceIds: string[] = form.watch('spaceIds') ?? [];

  const carrierSpaceId = resolveCarrierSpaceId(isTenantAdmin, lockedSpaceId, selectedSpaceIds);

  const carrierOptions = useCarrierOptions({
    carrierSpaceId,
    hasPermission,
    isDriverRole,
    lockedCarrierId,
    open,
    selectedSiteIds,
    user,
  });

  const vehicleFilterSiteIds = resolveVehicleFilterSiteIds(isEditMode, lockedSiteId, selectedSiteIds);
  const driverContext = resolveDriverContext(user);

  const driverVehicleField = useDriverVehicleField(
    open && isDriverRole,
    selectedCarrierId,
    hasPermission,
    driverContext.userId,
    vehicleFilterSiteIds,
    driverContext.defaultVehicleId,
    carrierSpaceId,
  );

  const siteFilterForSpace = useDriverSiteFilter(isDriverRole ? selectedCarrierId : undefined);
  const existingSiteOptions = useMemo(() => getExistingSiteScopeOptions(user), [user]);

  usePruneSitesOnCarrierChange({
    existingSiteOptions,
    form,
    open,
    originalCarrierId: driverContext.carrierId,
    selectedCarrierId,
    selectedSpaceIds,
    siteFilterForSpace,
  });

  useClearStaleVehicleSelection({
    form,
    open,
    vehicleOptions: driverVehicleField.vehicleOptions,
    vehiclesLoaded: driverVehicleField.vehiclesLoaded,
  });

  return { carrierOptions, driverVehicleField, siteFilterForSpace };
}

// ─── Carrier options ──────────────────────────────────────────────────────────

interface UseCarrierOptionsParams {
  carrierSpaceId: string | null;
  hasPermission: (permission: string) => boolean;
  isDriverRole: boolean;
  lockedCarrierId?: string;
  open: boolean;
  selectedSiteIds: string[];
  user: UserWithResolvedScopes | null;
}

// Carriers are scoped to the edited user's space; tenant-wide (no spaceId filter) whenever no
// space is selected — TENANT_ADMIN (all spaces) and "no space chosen yet" both resolve to a null
// carrierSpaceId and fall back to the same tenant-wide list, matching "carrier options follow the
// edited user's scope: no space selection = All Spaces = tenant-wide". The assigned carrier is
// fetched explicitly so a drifted/out-of-scope carrier stays selectable (escape hatch).
function useCarrierOptions({
  carrierSpaceId,
  hasPermission,
  isDriverRole,
  lockedCarrierId,
  open,
  selectedSiteIds,
  user,
}: UseCarrierOptionsParams): MultiSelectOption[] {
  const { data: carriersData } = useCarriers({
    enabled: resolveCarriersQueryEnabled({ hasPermission, isDriverRole, open }),
    page: 1,
    pageSize: DROPDOWN_FETCH_PAGE_SIZE,
    spaceId: carrierSpaceId ?? undefined,
  });

  const { data: assignedCarrier } = useCarrier(
    isDriverRole ? (user?.driverAttributes?.carrierId ?? lockedCarrierId) : undefined,
  );

  return useMemo<MultiSelectOption[]>(
    () =>
      withAssignedOption(
        filterCarriersByContext(carriersData?.items ?? [], carrierSpaceId, selectedSiteIds).map((carrier) => ({
          label: carrier.name,
          value: carrier.id,
        })),
        assignedCarrier,
        (carrier) => ({ label: carrier.name, value: carrier.id }),
      ),
    [carriersData, carrierSpaceId, selectedSiteIds, assignedCarrier],
  );
}

interface ResolveCarriersQueryEnabledParams {
  hasPermission: (permission: string) => boolean;
  isDriverRole: boolean;
  open: boolean;
}

// No longer gated on a selected space or TENANT_ADMIN: the query itself already falls back to the
// tenant-wide carrier list whenever carrierSpaceId is null (see spaceId: carrierSpaceId ?? undefined
// above), so gating enablement on carrierSpaceId/isTenantAdmin only produced an empty, unexplained
// dropdown for a genuinely tenant-wide driver invite (no space selected, not TENANT_ADMIN).
function resolveCarriersQueryEnabled({
  hasPermission,
  isDriverRole,
  open,
}: ResolveCarriersQueryEnabledParams): boolean {
  return open && isDriverRole && hasPermission('carriers:list');
}

/**
 * Filters carriers to those usable in the given context: enabled on ANY of the selected sites
 * (not just the first one). With no sites selected — or no space — all candidate carriers are
 * returned, since the space-wide list is already correctly scoped by the query.
 */
function filterCarriersByContext<T extends { enabledScopes: CarrierEnabledScope[] }>(
  carriers: T[],
  spaceId: string | null,
  siteIds: string[],
): T[] {
  if (!spaceId || siteIds.length === 0) return carriers;
  return carriers.filter((carrier) =>
    siteIds.some((siteId) => isCarrierEnabledOnSite(carrier.enabledScopes, siteId, spaceId)),
  );
}

function resolveCarrierSpaceId(
  isTenantAdmin: boolean,
  lockedSpaceId: string | undefined,
  selectedSpaceIds: string[],
): string | null {
  if (isTenantAdmin) return null;
  return lockedSpaceId ?? selectedSpaceIds[0] ?? null;
}

function resolveVehicleFilterSiteIds(
  isEditMode: boolean,
  lockedSiteId: string | undefined,
  selectedSiteIds: string[],
): string[] {
  if (!isEditMode && lockedSiteId) return [lockedSiteId];
  return selectedSiteIds;
}

interface DriverContext {
  carrierId: string | undefined;
  defaultVehicleId: string | null | undefined;
  userId: string | undefined;
}

function resolveDriverContext(user: UserWithResolvedScopes | null): DriverContext {
  return {
    carrierId: user?.driverAttributes?.carrierId,
    defaultVehicleId: user?.driverAttributes?.defaultVehicleId,
    userId: user?.id,
  };
}

// ─── Site filter (carrier-derived scope constraint) ──────────────────────────

// A user/driver can only be scoped to sites where the selected carrier is enabled. Resolves the
// selected carrier's enabled scopes into a per-space site filter for the scope selector.
function useDriverSiteFilter(carrierId: string | undefined): ((spaceId: string) => CarrierSiteFilter) | undefined {
  const { data: carrier } = useCarrier(carrierId);
  const enabledScopes = carrier?.enabledScopes;

  return useMemo(
    () =>
      enabledScopes
        ? (spaceId: string): CarrierSiteFilter => getCarrierSiteFilterForSpace(enabledScopes, spaceId)
        : undefined,
    [enabledScopes],
  );
}

// ─── Existing site scopes ─────────────────────────────────────────────────────

/** Resolved labels for the user's/driver's existing SITE-level scopes, keyed with their parent space. */
export function getExistingSiteScopeOptions(user: UserWithResolvedScopes | null): ExistingSiteOption[] {
  return (user?.resolvedScopes ?? [])
    .filter((scope) => scope.level === ROLE_SCOPE.SITE)
    .map((scope) => ({ label: scope.name, parentSpaceId: scope.parentSpaceId, value: scope.id }));
}

// ─── Effects ──────────────────────────────────────────────────────────────────

interface PruneSitesOnCarrierChangeParams {
  existingSiteOptions: ExistingSiteOption[];
  form: UseFormReturn<UserFormInput>;
  open: boolean;
  originalCarrierId: string | undefined;
  selectedCarrierId: string | undefined;
  selectedSpaceIds: string[];
  siteFilterForSpace: ((spaceId: string) => CarrierSiteFilter) | undefined;
}

// Prunes sites the carrier doesn't serve. While the carrier is unchanged, the persisted sites are
// exempt so a drifted scope is not silently dropped on open; changing the carrier lifts the exemption.
function usePruneSitesOnCarrierChange({
  existingSiteOptions,
  form,
  open,
  originalCarrierId,
  selectedCarrierId,
  selectedSpaceIds,
  siteFilterForSpace,
}: PruneSitesOnCarrierChangeParams): void {
  const persistedSiteIds = useMemo(() => existingSiteOptions.map((o) => o.value), [existingSiteOptions]);

  useEffect(() => {
    if (!open || !siteFilterForSpace) return;
    const currentSiteIds: string[] = form.getValues('siteIds') ?? [];
    if (currentSiteIds.length === 0) return;

    const exempt = selectedCarrierId === originalCarrierId ? new Set(persistedSiteIds) : new Set<string>();
    const siteToSpace = new Map<string, string>(
      existingSiteOptions.map((opt) => [opt.value, opt.parentSpaceId ?? selectedSpaceIds[0] ?? '']),
    );
    const prunedSiteIds = currentSiteIds.filter((siteId) =>
      isSiteStillAllowed(siteId, { exempt, selectedSpaceIds, siteFilterForSpace, siteToSpace }),
    );

    if (prunedSiteIds.length !== currentSiteIds.length) {
      form.setValue('siteIds', prunedSiteIds, { shouldDirty: true });
    }
  }, [
    open,
    siteFilterForSpace,
    selectedCarrierId,
    originalCarrierId,
    persistedSiteIds,
    existingSiteOptions,
    selectedSpaceIds,
    form,
  ]);
}

interface IsSiteStillAllowedContext {
  exempt: Set<string>;
  selectedSpaceIds: string[];
  siteFilterForSpace: (spaceId: string) => CarrierSiteFilter;
  siteToSpace: Map<string, string>;
}

function isSiteStillAllowed(siteId: string, ctx: IsSiteStillAllowedContext): boolean {
  if (ctx.exempt.has(siteId)) return true;
  const spaceId = ctx.siteToSpace.get(siteId) ?? ctx.selectedSpaceIds[0] ?? '';
  if (!spaceId) return true;
  const filter = ctx.siteFilterForSpace(spaceId);
  if (filter.mode === 'none') return false;
  if (filter.mode === 'only') return filter.ids.includes(siteId);
  return !filter.excludeIds.includes(siteId);
}

interface ClearStaleVehicleSelectionParams {
  form: UseFormReturn<UserFormInput>;
  open: boolean;
  vehicleOptions: MultiSelectOption[];
  vehiclesLoaded: boolean;
}

// Carrier and site selections both drive the vehicle dropdown's filter. When either changes, the
// selected vehicle may no longer match — clear it once the re-filtered options confirm it is gone.
function useClearStaleVehicleSelection({
  form,
  open,
  vehicleOptions,
  vehiclesLoaded,
}: ClearStaleVehicleSelectionParams): void {
  const selectedVehicleId = form.watch('defaultVehicleId');

  useEffect(() => {
    if (!open || !vehiclesLoaded || !selectedVehicleId) return;
    if (!vehicleOptions.some((opt) => opt.value === selectedVehicleId)) {
      form.setValue('defaultVehicleId', null, { shouldDirty: true });
    }
  }, [open, vehiclesLoaded, vehicleOptions, selectedVehicleId, form]);
}
